import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Trash2, Upload, Music, Loader2, FolderPlus, Folder, Image, X, ArrowLeft, GripVertical, MoreVertical, Edit2, Check, Lock, Unlock } from 'lucide-react';
import { useZen } from '../contexts/ZenContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TrackList = () => {
  const [tracks, setTracks] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: 'mp3' });
  const fileInputRef = useRef(null);
  const folderImageRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  
  // Folder states
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderImage, setNewFolderImage] = useState(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');
  
  // Drag and drop for tracks
  const [draggingTrack, setDraggingTrack] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);

  const { currentTrack, isPlaying, playTrack, togglePlay } = useZen();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tracksRes, foldersRes] = await Promise.all([
        axios.get(`${API}/tracks`),
        axios.get(`${API}/audio-folders`)
      ]);
      setTracks(tracksRes.data);
      setFolders(foldersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!uploadForm.title) {
        setUploadForm(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('audio/')) {
      setSelectedFile(file);
      if (!uploadForm.title) {
        setUploadForm(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.title) {
      toast.error('Veuillez sélectionner un fichier et entrer un titre');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('title', uploadForm.title);
    formData.append('description', uploadForm.description || '');
    formData.append('category', uploadForm.category);

    try {
      const response = await axios.post(`${API}/tracks/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Audio uploadé avec succès');
      
      // If in a folder, add track to folder
      if (selectedFolder) {
        await axios.post(`${API}/audio-folders/${selectedFolder.id}/tracks/${response.data.id}`);
      }
      
      setSelectedFile(null);
      setUploadForm({ title: '', description: '', category: 'mp3' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (error) {
      console.error('Error uploading track:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (trackId) => {
    if (!window.confirm('Supprimer cette piste ?')) return;
    
    try {
      await axios.delete(`${API}/tracks/${trackId}`);
      toast.success('Piste supprimée');
      fetchData();
    } catch (error) {
      console.error('Error deleting track:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePlayTrack = (track) => {
    if (currentTrack?.id === track.id) {
      togglePlay();
    } else {
      playTrack(track);
    }
  };

  // Folder functions
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Veuillez entrer un nom pour le dossier');
      return;
    }

    setCreatingFolder(true);
    const formData = new FormData();
    formData.append('name', newFolderName);
    if (newFolderImage) {
      formData.append('image', newFolderImage);
    }

    try {
      await axios.post(`${API}/audio-folders`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Dossier créé');
      setNewFolderName('');
      setNewFolderImage(null);
      setShowCreateFolder(false);
      fetchData();
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Erreur lors de la création du dossier');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    const folder = folders.find(f => f.id === folderId);
    if (folder?.locked) {
      toast.error('Ce dossier est verrouillé. Déverrouillez-le d\'abord pour le supprimer.');
      return;
    }
    if (!window.confirm('Supprimer ce dossier ? Les audios ne seront pas supprimés.')) return;
    
    try {
      await axios.delete(`${API}/audio-folders/${folderId}`);
      toast.success('Dossier supprimé');
      if (selectedFolder?.id === folderId) {
        setSelectedFolder(null);
      }
      fetchData();
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleUpdateFolder = async (folderId) => {
    if (!editFolderName.trim()) return;
    
    const formData = new FormData();
    formData.append('name', editFolderName);

    try {
      await axios.put(`${API}/audio-folders/${folderId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Dossier renommé');
      setEditingFolder(null);
      fetchData();
    } catch (error) {
      console.error('Error updating folder:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleToggleLock = async (folder) => {
    const newLocked = !folder.locked;
    const formData = new FormData();
    formData.append('locked', newLocked.toString());

    try {
      await axios.put(`${API}/audio-folders/${folder.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(newLocked ? 'Dossier verrouillé' : 'Dossier déverrouillé');
      fetchData();
    } catch (error) {
      console.error('Error toggling lock:', error);
      toast.error('Erreur lors du changement de verrouillage');
    }
  };

  // Drag and drop handlers
  const handleTrackDragStart = (e, track) => {
    setDraggingTrack(track);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFolderDragOver = (e, folder) => {
    e.preventDefault();
    setDragOverFolder(folder.id);
  };

  const handleFolderDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleFolderDrop = async (e, folder) => {
    e.preventDefault();
    setDragOverFolder(null);
    
    if (draggingTrack) {
      try {
        await axios.post(`${API}/audio-folders/${folder.id}/tracks/${draggingTrack.id}`);
        toast.success(`"${draggingTrack.title}" ajouté à "${folder.name}"`);
        fetchData();
      } catch (error) {
        console.error('Error adding track to folder:', error);
        toast.error('Erreur lors de l\'ajout au dossier');
      }
    }
    setDraggingTrack(null);
  };

  const handleRemoveFromFolder = async (folderId, trackId) => {
    try {
      await axios.delete(`${API}/audio-folders/${folderId}/tracks/${trackId}`);
      toast.success('Audio retiré du dossier');
      fetchData();
    } catch (error) {
      console.error('Error removing track from folder:', error);
      toast.error('Erreur lors du retrait');
    }
  };

  // Get tracks for current view
  const getTracksForView = () => {
    if (selectedFolder) {
      return tracks.filter(t => selectedFolder.track_ids?.includes(t.id));
    }
    // Show only tracks not in any folder
    const allFolderTrackIds = folders.flatMap(f => f.track_ids || []);
    return tracks.filter(t => !allFolderTrackIds.includes(t.id));
  };

  const visibleTracks = getTracksForView();

  return (
    <div className="space-y-6" data-testid="track-list">
      {/* Folders Section */}
      <div className="player-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
            {selectedFolder ? (
              <>
                <button 
                  onClick={() => setSelectedFolder(null)}
                  className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <span>{selectedFolder.name}</span>
              </>
            ) : (
              <>
                <Folder className="w-5 h-5 text-amber-400" />
                Mes Dossiers
              </>
            )}
          </h3>
          
          {!selectedFolder && (
            <Button
              onClick={() => setShowCreateFolder(!showCreateFolder)}
              variant="ghost"
              size="sm"
              className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              Nouveau dossier
            </Button>
          )}
        </div>

        {/* Create Folder Form */}
        {showCreateFolder && !selectedFolder && (
          <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10 space-y-4">
            <div className="flex items-center gap-4">
              {/* Folder Image Preview */}
              <label className="cursor-pointer group">
                <input
                  ref={folderImageRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setNewFolderImage(e.target.files?.[0])}
                />
                <div className={`w-20 h-20 rounded-xl flex items-center justify-center transition-all ${
                  newFolderImage 
                    ? 'bg-cover bg-center' 
                    : 'bg-white/10 border-2 border-dashed border-white/30 group-hover:border-amber-400'
                }`}
                style={newFolderImage ? { backgroundImage: `url(${URL.createObjectURL(newFolderImage)})` } : {}}
                >
                  {!newFolderImage && (
                    <Image className="w-8 h-8 text-white/40 group-hover:text-amber-400" />
                  )}
                </div>
              </label>
              
              <div className="flex-1">
                <Input
                  placeholder="Nom du dossier *"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 mb-2"
                />
                <p className="text-white/40 text-xs">Cliquez sur l'image pour ajouter une couverture</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleCreateFolder}
                disabled={creatingFolder}
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600"
              >
                {creatingFolder ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
              </Button>
              <Button
                onClick={() => {
                  setShowCreateFolder(false);
                  setNewFolderName('');
                  setNewFolderImage(null);
                }}
                variant="ghost"
                className="text-white/60"
              >
                Annuler
              </Button>
            </div>
          </div>
        )}

        {/* Folders Grid */}
        {!selectedFolder && folders.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-6">
            {folders.map((folder) => (
              <div
                key={folder.id}
                className={`group relative cursor-pointer transition-all ${
                  dragOverFolder === folder.id ? 'scale-105' : ''
                }`}
                onDragOver={(e) => handleFolderDragOver(e, folder)}
                onDragLeave={handleFolderDragLeave}
                onDrop={(e) => handleFolderDrop(e, folder)}
                onClick={() => setSelectedFolder(folder)}
              >
                {/* Folder Cover */}
                <div className={`aspect-square rounded-xl overflow-hidden mb-2 transition-all ${
                  dragOverFolder === folder.id 
                    ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-900' 
                    : 'group-hover:ring-2 group-hover:ring-white/30'
                }`}>
                  {folder.image_url ? (
                    <img 
                      src={folder.image_url.startsWith('http') ? folder.image_url : `${process.env.REACT_APP_BACKEND_URL}${folder.image_url}`}
                      alt={folder.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
                      <Folder className="w-12 h-12 text-white/30" />
                    </div>
                  )}
                </div>
                
                {/* Folder Name */}
                {editingFolder === folder.id ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={editFolderName}
                      onChange={(e) => setEditFolderName(e.target.value)}
                      className="h-7 text-sm bg-white/10 border-white/20"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.key === 'Enter' && handleUpdateFolder(folder.id)}
                      autoFocus
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateFolder(folder.id);
                      }}
                      className="p-1 text-amber-400 hover:bg-amber-500/20 rounded"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-white text-sm font-medium truncate">{folder.name}</p>
                )}
                <p className="text-white/40 text-xs">{folder.track_ids?.length || 0} audio{(folder.track_ids?.length || 0) > 1 ? 's' : ''}</p>
                
                {/* Folder Actions */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingFolder(folder.id);
                      setEditFolderName(folder.name);
                    }}
                    className="p-1.5 bg-black/60 rounded-lg text-white/80 hover:text-white hover:bg-black/80"
                    title="Renommer"
                    data-testid={`rename-folder-${folder.id}`}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleLock(folder);
                    }}
                    className={`p-1.5 bg-black/60 rounded-lg hover:bg-black/80 transition-colors ${
                      folder.locked ? 'text-yellow-400 hover:text-yellow-300' : 'text-white/80 hover:text-yellow-400'
                    }`}
                    title={folder.locked ? 'Déverrouiller' : 'Verrouiller'}
                    data-testid={`toggle-lock-folder-${folder.id}`}
                  >
                    {folder.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                  </button>
                  {!folder.locked && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.id);
                      }}
                      className="p-1.5 bg-black/60 rounded-lg text-white/80 hover:text-red-400 hover:bg-black/80"
                      title="Supprimer"
                      data-testid={`delete-folder-${folder.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Track List */}
        <div>
          <h4 className="text-white/60 text-sm uppercase tracking-wider mb-3">
            {selectedFolder ? `Audios dans "${selectedFolder.name}"` : 'Audios non classés'}
          </h4>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
            </div>
          ) : visibleTracks.length === 0 ? (
            <p className="text-white/40 text-center py-8">
              {selectedFolder 
                ? 'Aucun audio dans ce dossier. Glissez-déposez des audios ici !'
                : folders.length > 0 
                  ? 'Tous vos audios sont dans des dossiers'
                  : 'Aucun audio. Uploadez votre premier fichier !'
              }
            </p>
          ) : (
            <div className="space-y-2">
              {visibleTracks.map((track) => {
                const isCurrentTrack = currentTrack?.id === track.id;
                const isTrackPlaying = isCurrentTrack && isPlaying;
                
                return (
                  <div
                    key={track.id}
                    draggable={!selectedFolder}
                    onDragStart={(e) => handleTrackDragStart(e, track)}
                    onDragEnd={() => setDraggingTrack(null)}
                    className={`track-item ${isCurrentTrack ? 'playing' : ''} ${
                      draggingTrack?.id === track.id ? 'opacity-50' : ''
                    }`}
                    data-testid={`track-item-${track.id}`}
                  >
                    {!selectedFolder && (
                      <GripVertical className="w-4 h-4 text-white/30 cursor-grab flex-shrink-0" />
                    )}
                    
                    <button
                      onClick={() => handlePlayTrack(track)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                        isCurrentTrack
                          ? 'bg-amber-500 text-slate-900'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                      data-testid={`play-track-${track.id}`}
                    >
                      {isTrackPlaying ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4 ml-0.5" />
                      )}
                    </button>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{track.title}</p>
                      {track.description && (
                        <p className="text-white/50 text-sm truncate">{track.description}</p>
                      )}
                    </div>
                    
                    <Music className="w-4 h-4 text-white/30 flex-shrink-0" />
                    
                    {selectedFolder && (
                      <button
                        onClick={() => handleRemoveFromFolder(selectedFolder.id, track.id)}
                        className="p-2 text-white/40 hover:text-amber-500 hover:bg-amber-500/10 rounded-lg transition-all"
                        title="Retirer du dossier"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    
                    {!track.locked && (
                      <button
                        onClick={() => handleDelete(track.id)}
                        className="p-2 text-white/40 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                        title="Supprimer cet audio"
                        data-testid={`delete-track-${track.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                    {track.locked && (
                      <div className="p-2 text-amber-500/60" title="Audio verrouillé">
                        <Lock className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Upload Zone (moved below Mes Dossiers) */}
      <div className="player-card p-6">
        <h3 className="font-heading text-lg font-semibold text-white mb-4">
          Ajouter un Audio {selectedFolder && <span className="text-amber-400">dans "{selectedFolder.name}"</span>}
        </h3>
        
        <div
          className={`upload-zone mb-4 ${dragOver ? 'dragover' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid="upload-zone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          <Upload className="w-10 h-10 text-white/40 mx-auto mb-3" />
          {selectedFile ? (
            <p className="text-amber-400">{selectedFile.name}</p>
          ) : (
            <p className="text-white/60">
              Glissez un fichier audio ou cliquez pour sélectionner
            </p>
          )}
          <p className="text-white/40 text-sm mt-2">MP3, WAV, OGG, M4A</p>
        </div>

        {selectedFile && (
          <div className="space-y-3">
            <Input
              placeholder="Titre de l'audio *"
              value={uploadForm.title}
              onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              data-testid="track-title-input"
            />
            <Input
              placeholder="Description (optionnel)"
              value={uploadForm.description}
              onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              data-testid="track-description-input"
            />
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500"
              data-testid="upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Upload en cours...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Uploader
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Drag hint */}
      {!selectedFolder && tracks.length > 0 && folders.length > 0 && (
        <p className="text-white/30 text-xs text-center">
          Astuce: Glissez-déposez les audios sur un dossier pour les organiser
        </p>
      )}
    </div>
  );
};

export default TrackList;
